/**
 * Aleatoriedade determinística do mundo — §6.1 da especificação.
 *
 * Tudo aqui opera em inteiros de 32 bits via operadores bitwise, que são
 * exatos e idênticos em qualquer motor JS. Nenhuma função transcendental,
 * nenhum ponto flutuante no caminho do estado.
 */
/** Hash FNV-1a de 32 bits sobre uma string ASCII. Usado para derivar seeds. */
export function fnv1a(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i++) {
        hash ^= text.charCodeAt(i) & 0xff;
        // hash *= 16777619, escrito como somas de deslocamentos para não estourar
        // a precisão de double antes do >>> 0.
        hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
    }
    return hash >>> 0;
}
/** Mistura dois inteiros de 32 bits num terceiro. Deriva sub-seeds de chunk. */
export function mixSeed(a, b) {
    let h = (a ^ 0x9e3779b9) >>> 0;
    h = (h ^ (b + 0x9e3779b9 + ((h << 6) >>> 0) + (h >>> 2))) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    h = Math.imul(h, 0x85ebca6b) >>> 0;
    h = (h ^ (h >>> 13)) >>> 0;
    h = Math.imul(h, 0xc2b2ae35) >>> 0;
    return (h ^ (h >>> 16)) >>> 0;
}
/**
 * Gerador de números pseudoaleatórios do mundo.
 *
 * Mulberry32: estado de 32 bits, período 2^32, distribuição adequada para
 * colocação de terreno e entidades. Escolhido por ser curto o bastante para
 * auditar de olho — o que importa quando um bug aqui vira dessincronização
 * de rede e replay rejeitado.
 */
export class RngService {
    state;
    constructor(seed) {
        this.state = seed >>> 0;
    }
    /** Estado interno, para snapshot e checksum. */
    get snapshot() {
        return this.state >>> 0;
    }
    /** Restaura um estado capturado — usado no rewind da reconciliação. */
    restore(state) {
        this.state = state >>> 0;
    }
    /** Próximo inteiro sem sinal de 32 bits. */
    nextUint32() {
        this.state = (this.state + 0x6d2b79f5) >>> 0;
        let t = this.state;
        t = Math.imul(t ^ (t >>> 15), t | 1) >>> 0;
        t = (t ^ (t + Math.imul(t ^ (t >>> 7), t | 61))) >>> 0;
        return (t ^ (t >>> 14)) >>> 0;
    }
    /**
     * Inteiro uniforme em `[min, max]`, ambos inclusivos.
     *
     * Usa rejeição para evitar o viés do módulo: sem isso, os primeiros valores
     * da faixa saem com probabilidade ligeiramente maior, o que enviesa largura
     * de rio e posição de entidade de um jeito que só aparece em playtest longo.
     */
    intBetween(min, max) {
        if (max <= min)
            return min;
        const range = max - min + 1;
        const limit = 0x100000000 - (0x100000000 % range);
        let value = this.nextUint32();
        while (value >= limit)
            value = this.nextUint32();
        return min + (value % range);
    }
    /** `true` com probabilidade `numerator / denominator`. Sem float. */
    chance(numerator, denominator) {
        if (numerator <= 0)
            return false;
        if (numerator >= denominator)
            return true;
        return this.intBetween(0, denominator - 1) < numerator;
    }
    /** Deriva um gerador filho, independente e reproduzível. */
    fork(salt) {
        return new RngService(mixSeed(this.state, salt));
    }
}
/** Seed do Desafio Diário — §6.1. `isoDate` no formato `AAAA-MM-DD`, em UTC. */
export function dailySeed(isoDate) {
    return fnv1a(`pixel-raid:${isoDate}`);
}
/** Sub-seed de um chunk procedural — §6.1. */
export function chunkSeed(worldSeed, chunkIndex) {
    return mixSeed(worldSeed, chunkIndex);
}
//# sourceMappingURL=rng.js.map